import { gql, useQuery } from "@apollo/client";
import { useContext } from "react";
import { DateTime } from "luxon";
import { LoadingView, NotificationContext, Tabs } from "@solsys/solsys-reactjs";
import { useUserList } from "../../contexts/UserListContext";
import { isTechnician } from "../../utils/roles";
import { useAuth } from "../../contexts/AuthContext";
import TicketEdit from "../ticket/TicketEdit";
import ServiceEdit from "../service/ServiceEdit";
import TechnicianServiceInterface from "../service/TechnicianServiceInterface";

const entityQuery = gql`
  query partDetail($service_part_list_request: service_part_list_request) {
    service_part(service_part_list_request: $service_part_list_request) {
      edges {
        id
        part_no
        quantity
        description
        created_at
        updated_at
        ticket_id
        service_id
        crane_id
        user_id
        crane {
          id
          name
        }
        ticket {
          id
          name
        }
        service {
          id
          service_definition {
            name
          }
        }
      }
    }
  }
`;

function formatDateTime(iso) {
  if (!iso) return "—";
  const dt = DateTime.fromISO(iso, { zone: "utc" });
  return dt.isValid ? dt.toLocal().toFormat("MMM d, yyyy h:mm a") : "—";
}

export default function PartEdit({ id: entityId }) {
  const { addNotification } = useContext(NotificationContext);
  const { user } = useAuth();
  const userList = useUserList();
  const isTech = isTechnician(user);
  const ServiceComponent = isTech ? TechnicianServiceInterface : ServiceEdit;

  const { loading, data } = useQuery(entityQuery, {
    variables: {
      service_part_list_request: {
        filters: [{ field: "id", operator: "=", value: String(entityId ?? 0) }],
        pagination: { limit: 1 },
      },
    },
    skip: !entityId,
    fetchPolicy: "network-only",
    onError: () => addNotification("bad", "Failed to load part"),
  });

  const part = data?.service_part?.edges?.[0];

  if (loading) return <LoadingView />;
  if (!part) {
    return <div style={ { padding: "1rem" } }>Part not found.</div>;
  }

  const isTicket = !!part.ticket_id;
  const isService = !!part.service_id;
  const sourceType = isTicket ? "Ticket" : isService ? "Service" : "Unknown";
  const foundUser = userList.find((u) => String(u._id) === String(part.user_id));
  const techName = foundUser?.name ?? foundUser?.username ?? "—";
  const noopNavigate = () => {};

  const relatedLabel = isTicket ? "Ticket" : "Service";

  return (
    <>
      <div className="page-header">
        <span className="page-header-title">Part Detail</span>
      </div>

      <div className="add-edit">
        <div className="edit-section">
          <div className="edit-property">
            <label>Part ID:</label>
            <input value={ part.id } readOnly disabled />
          </div>
          <div className="edit-property">
            <label>Part Number:</label>
            <input value={ part.part_no || "—" } readOnly disabled />
          </div>
          <div className="edit-property">
            <label>Quantity:</label>
            <input value={ part.quantity ?? "—" } readOnly disabled />
          </div>
          <div className="edit-property">
            <label>Source:</label>
            <input value={ sourceType } readOnly disabled />
          </div>
          <div className="edit-property">
            <label>Crane:</label>
            <input
              value={ part.crane?.name ? `${part.crane.name} (#${part.crane.id})` : "—" }
              readOnly
              disabled
            />
          </div>
          <div className="edit-property">
            <label>Technician:</label>
            <input value={ techName } readOnly disabled />
          </div>
          <div className="edit-property">
            <label>Added:</label>
            <input value={ formatDateTime(part.created_at) } readOnly disabled />
          </div>
          { part.description && (
            <div className="edit-property full-width">
              <label>Description:</label>
              <textarea value={ part.description } readOnly disabled rows={ 3 } />
            </div>
          ) }
          <div className="edit-property full-width">
            <label>Related record:</label>
            <input
              value={
                isTicket
                  ? (part.ticket?.name
                    ? `Ticket #${part.ticket_id} — ${part.ticket.name}`
                    : `Ticket #${part.ticket_id}`)
                  : isService
                    ? (part.service?.service_definition?.name
                      ? `Service #${part.service_id} — ${part.service.service_definition.name}`
                      : `Service #${part.service_id}`)
                    : "—"
              }
              readOnly
              disabled
            />
          </div>
        </div>
      </div>

      { (isTicket || isService) && (
        <div className="ticket-edit-tabs">
          <Tabs autoSelect>
            { isTicket ? (
              <div label={ relatedLabel }>
                <TicketEdit
                  key={ `part-ticket-${part.ticket_id}` }
                  id={ part.ticket_id }
                  action="edit"
                  navigate={ noopNavigate }
                  readOnly
                />
              </div>
            ) : (
              <div label={ relatedLabel }>
                <ServiceComponent
                  key={ `part-service-${part.service_id}` }
                  id={ part.service_id }
                  action="edit"
                  navigate={ noopNavigate }
                  readOnly
                />
              </div>
            ) }
          </Tabs>
        </div>
      ) }
    </>
  );
}
